import * as dayjs from 'dayjs';
import { LIBRARY_TYPES, SORT_OPTIONS } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { FullstoryService, fullstoryToken } from 'go-modules/services/fullstory/fullstory.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { Component, ElementRef, EventEmitter, HostBinding, Inject, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { LibraryCollectionFolderItemCount, LibraryService } from '@go-modules/services/library/library.service';
import { MessageDialogComponent, MessageDialogData } from '../../dialogs/message-dialog/message-dialog.component';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { ManageLibraryCollectionFolderComponent } from '../manage-library-collection-folder/manage-library-collection-folder.component';
import type { LibraryCollection, LibraryFolder, LibraryCollectionItemsAndFolders } from '@go-modules/interfaces/library/library-collection';
import type { ContentTypeType } from '@go-modules/interfaces/library/library-types';
import { LibraryTreeDataSource } from '@go-modules/components/library/library-tree-viewer/library-tree-datasource';
import type { LibraryDialogContext } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';

@Component({
	selector: 'ngx-collection-folder-viewer, tr[ngxCollectionFolderRow]',
	templateUrl: './collection-folder-viewer.component.html',
	styleUrls: ['./collection-folder-viewer.component.scss'],
	standalone: false
})
export class CollectionFolderViewerComponent {
	@Input() public folder: LibraryFolder;
	@Input() public mode: string;
	@Output() public selectItem: EventEmitter<any> = new EventEmitter();
	@Input() public collections: LibraryCollection[];
	@Input() public archiveCollection: LibraryCollection;
	@Input() public selectedCollection: LibraryCollection;
	@Input() public selectedContentType: ContentTypeType;
	@Output() public loadNewItems: EventEmitter<any> = new EventEmitter();
	@Input() public collectionItems: LibraryCollectionItemsAndFolders;
	@Input() public sortBy: string;
	@Input() public searchStr: string;
	@Input() public multiSelect: boolean;
	@Output() public setSelectAllChecked: EventEmitter<boolean> = new EventEmitter();
	@Input() public expandedHeight: number;
	@Input() public currentlySelected: any;
	@Output() public setActivityPanelIsOpen: EventEmitter<boolean> = new EventEmitter();
	@Input() public orgSettings: any;
	@Input() public context?: LibraryDialogContext | null;
	@Output() public setCurrentlySelected: EventEmitter<any> = new EventEmitter();

	@Input() public nodeLevel = 0;
	@Input() public treeGridNameTabindex: number | null = null;
	@Input() public treeGridModifiedTabindex: number | null = null;
	@Input() public treeGridActionTabindex: number | null = null;
	@Input() public treeGridSelectHeaderId: string | null = null;
	@Input() public treeGridNameHeaderId: string | null = null;
	@Input() public treeGridModifiedHeaderId: string | null = null;
	@Output() public treeGridFocus: EventEmitter<'name' | 'modified' | 'actions'> = new EventEmitter();
	@Output() public treeGridKeydown: EventEmitter<{
		event: KeyboardEvent;
		target: 'name' | 'modified' | 'actions';
	}> = new EventEmitter();

	public isTreeTableRowHost (): boolean {
		return this.elementRef.nativeElement.tagName === 'TR';
	}

	public emitTreeGridFocus (target: 'name' | 'modified' | 'actions'): void {
		if (!this.isTreeTableRowHost()) {
			return;
		}

		this.treeGridFocus.emit(target);
	}

	public emitTreeGridKeydown (event: KeyboardEvent, target: 'name' | 'modified' | 'actions'): void {
		if (!this.isTreeTableRowHost()) {
			return;
		}

		this.treeGridKeydown.emit({ event, target });
	}

	@HostBinding('class.tree-table-row')
	public get hostTreeTableRowClass (): boolean {
		return this.isTreeTableRowHost();
	}

	@HostBinding('class.active')
	public get hostActiveClass (): boolean {
		return this.isTreeTableRowHost() && this.currentlySelected?.id === this.folder?.id;
	}

	constructor (
		private elementRef: ElementRef,
		@Inject(fullstoryToken) private fullstoryService: FullstoryService,
		private dialog: MatDialog,
		private libraryService: LibraryService,
		private translate: TranslateService,
		@Inject(userServiceToken) private userService: UserService,
		private modal: GoModalService,
		public dataSource: LibraryTreeDataSource
	) {}

	public managedCollectionAccess (): boolean {
		if (this.selectedCollection.type === LIBRARY_TYPES.MANAGED) {
			return this.userService.currentUser.is_root_content;
		}
		//if not a managed collection just return true
		return true;
	}

	public getAriaLabel (): string {
		return this.translate.instant('collection-folder-aria-label', {
			name: this.folder.name,
			date: this.formatDate(this.folder.updated_at)
		});
	}

	public getMenuAriaLabel (): string {
		return `${this.translate.instant('collection-folder-menu')}, ${this.folder.name}`;
	}

	public getTreeGridNameElementId (): string {
		return `library-tree-folder-name-${this.folder.id}`;
	}

	public getTreeGridModifiedElementId (): string {
		return `library-tree-folder-modified-${this.folder.id}`;
	}

	public getTreeGridNameLabelledBy (): string | null {
		if (this.treeGridNameHeaderId == null) {
			return null;
		}

		return `${this.treeGridNameHeaderId} ${this.getTreeGridNameElementId()}`;
	}

	public getTreeGridModifiedLabelledBy (): string | null {
		if (this.treeGridModifiedHeaderId == null) {
			return null;
		}

		return `${this.treeGridModifiedHeaderId} ${this.getTreeGridModifiedElementId()}`;
	}

	public formatDate (date) {
		return dayjs(date).format('MMM D, YYYY');
	}

	public getSortKey () {
		let itemField: string = 'name';

		if (this.sortBy === SORT_OPTIONS.ALPHANUMERICREVERSE) {
			itemField = '-name';
		} else if (this.sortBy === SORT_OPTIONS.LASTMODIFIED) {
			itemField = '-modified_at';
		}  else if (this.sortBy === SORT_OPTIONS.NEWEST) {
			itemField = '-created_at';
		} else if (this.sortBy === SORT_OPTIONS.OLDEST) {
			itemField = 'created_at';
		}

		return itemField;
	}

	public isExpanded (): boolean {
		return this.dataSource.isFolderExpanded(this.folder.id);
	}

	public expandFolder (folder: LibraryFolder): void {
		const id = folder.id;
		if (!this.dataSource.isFolderExpanded(id)) {
			this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_FOLDER_OPENED, {});
		}
		this.dataSource.toggleFolder(id);
		this.setCurrentlySelected.emit({id});
	}

	public removeFolder (folder: LibraryFolder) {
		return this.libraryService.getCollectionFolderItemCount(folder.id).pipe(
			catchError(() => of(null)), // if item counts fail, continue with null
			switchMap((itemCounts) => {
				if (itemCounts?.folders > 0) {
					return this.dialog.open(
						MessageDialogComponent, {
							data: {
								title: this.translate.instant('modal-remove-collection-folder_title'),
								message: this.translate.instant('modal-remove-collection-folder_message-error', {
									folderCount: itemCounts.folders
								}),
								cancelText: this.translate.instant('common_cancel')
							} as MessageDialogData
						}).afterClosed().pipe(map(()=> false));
				} else {
					const message = this.buildConfirmationMessage(itemCounts);
					return this.dialog.open(
						ConfirmDialogComponent, {
							data: {
								title: this.translate.instant('modal-remove-collection-folder_title'),
								message,
								confirmText: this.translate.instant('common_remove'),
								cancelText: this.translate.instant('common_cancel')
							} as ConfirmDialogData
						}).afterClosed();
				}
			}),
			filter((confirm: boolean) => confirm),
			switchMap(() => this.libraryService.removeFolder(folder.id))
		).subscribe({
			next: () => {
				this.dataSource.removeFolder(folder.id);
			},
			error: () => {
				MessageDialogComponent.open(this.dialog, {
					data: {
						title: this.translate.instant('modal-remove-collection-folder_error-title'),
						message: this.translate.instant('modal-remove-collection-folder_error-message'),
						confirmText: this.translate.instant('common_close')
					} as MessageDialogData
				});
			}
		});
	}

	public openEditFolderModal () {
		return this.modal.open(ManageLibraryCollectionFolderComponent, false, {
			data: {
				createMode: false,
				existingFolders: [],
				folder: this.folder
			}
		}).afterClosed().subscribe((res) => {
			if (res.dismissed) {
				return;
			}
			if (res.request_failed) {
				return MessageDialogComponent.open(this.dialog, {
					data: {
						title: this.translate.instant('library-collections_edit-folder-fail-title'),
						message: this.translate.instant('library-collections_edit-folder-fail-message'),
						confirmText: this.translate.instant('common_close')
					} as MessageDialogData
				});
			}
			this.folder.name = res.name;
			this.folder.updated_at = res.updated_at;
			this.dataSource.updateFolder({
				...this.folder
			});
		});
	}

	private buildConfirmationMessage (itemCounts: LibraryCollectionFolderItemCount): string {
		if (itemCounts) {
			const hasItems = itemCounts.media ||
				itemCounts.rubrics ||
				itemCounts.markers ||
				itemCounts.documents ||
				itemCounts.activities ||
				itemCounts.folders;

			if (hasItems) {
				return this.translate.instant('modal-remove-collection-folder_message-with-items', {
					media: itemCounts.media,
					rubrics: itemCounts.rubrics,
					markers: itemCounts.markers,
					documents: itemCounts.documents,
					activities: itemCounts.activities,
					folders: itemCounts.folders
				});
			}
		}

		return this.translate.instant('modal-remove-collection-folder_message');
	}
}
